// В.1.a / В.1.d: Молчание (Silence) — диздок 6.7.
//
// Молчание лишает юнит всех текстовых способностей и ключевых слов на момент
// наложения. Не меняет базовые atk/armor/hp. Не откатывает одноразовые эффекты,
// уже применённые к другим картам. Не блокирует эффекты, применяемые к
// молчащему юниту ПОСЛЕ от других карт (новые баффы работают как обычно).
//
// В.1.d: МУЛЬТИ-ИСТОЧНИКОВАЯ модель.
//   card.silenceSources — Set<string> идентификаторов источников Молчания.
//   card.current.silenced === card.silenceSources.size > 0.
//   Деструктивные эффекты (детач Модов, очистка keywords/derivedKeywords/
//   persistentKeywords) применяются ОДИН РАЗ при ПЕРВОМ источнике; повторные
//   applySilence (тот же или другой источник) только добавляют в set.
//   removeSilence(state, card, sourceId) убирает источник; когда set
//   опустевает — silenced=false. KEYWORDS И MODS НЕ ВОССТАНАВЛИВАЮТСЯ —
//   снятие Молчания только разрешает текстовым способностям снова работать.
//
// Назначение мульти-источника:
//   - Генератор помех (Глобал, В.1.d) — Аура «юниты напротив получают
//     Молчание, пока напротив». Источник: `generator:<instanceId>`.
//   - Нетраннер-адъютант (Глобал, В.1.c-2) — Молчание до конца игры.
//     Источник: 'netranner_adyutant'.
//   - Сапёр (Глобал, В.1.d) — Молчание до конца игры. Источник: 'saper'.

import { detachMod } from '../abilities/syntetics/modifications.js';
// C.1: Цепь командования удалена. Подкрепление пересчитывается отдельно
// через keywords/reinforcement.js (новая фракционная аура Глобала).
import { recomputeLineTriggers } from './line-triggers.js';
import { clearImmunityOnSilence } from './immune.js';
import { clearInfectedOnSilence } from './infected.js';
import { recomputeReinforcement } from './reinforcement.js';

// В.1.d: late-binding для пересчёта Ауры Генератора помех.
// Циклический импорт generator_pomeh.js → silence.js → generator_pomeh.js
// возможен при загрузке модулей. Используем `var` (hoisted с undefined,
// без TDZ), чтобы registerSilenceChangeListener мог быть вызван даже когда
// silence.js ещё не дошёл до строки инициализации массива.
// eslint-disable-next-line no-var
var _silenceChangeListeners;
export function registerSilenceChangeListener(fn) {
  if (!_silenceChangeListeners) _silenceChangeListeners = [];
  if (typeof fn === 'function') _silenceChangeListeners.push(fn);
}
function notifySilenceChange(state) {
  if (!_silenceChangeListeners) return;
  for (const fn of _silenceChangeListeners) {
    try { fn(state); } catch { /* defensive */ }
  }
}

// Применить Молчание к юниту или Герою с указанным источником.
// Возвращает { ok, alreadySilenced? }.
// В.2.g: разрешено также для Героев (раньше только юниты) — Герои Лив Лайф
// имеют пассивки/триггеры, которые должны блокироваться Молчанием.
// Деструктивы (детач Модов, очистка keywords) на Герое — no-op в большинстве
// случаев (Героев не модифицируют Модами, у них пустые derivedKeywords).
export function applySilence(state, card, sourceId = 'default') {
  if (!card) return { ok: false, reason: 'no_target' };
  if (card.type !== 'unit' && card.type !== 'hero') {
    return { ok: false, reason: 'not_unit_nor_hero' };
  }
  if (card.type === 'unit' && (!card.isAlive || !card.isAlive())) {
    return { ok: false, reason: 'dead' };
  }
  if (card.type === 'hero' && (card.hp == null || card.hp <= 0)) {
    return { ok: false, reason: 'dead' };
  }
  if (!card.silenceSources) card.silenceSources = new Set();
  if (card.silenceSources.has(sourceId)) {
    return { ok: true, alreadySilenced: true };
  }
  const wasSilenced = card.silenceSources.size > 0;
  card.silenceSources.add(sourceId);
  if (!card.current) card.current = {};
  card.current.silenced = true;

  if (wasSilenced) {
    // Юнит уже был под Молчанием от другого источника — деструктив не повторяем.
    return { ok: true, alreadySilenced: true };
  }

  // 1) Снять Моды (Синтетикс) — detachMod откатит стат-бонусы и keyword'ы Модов.
  if (Array.isArray(card.modifications) && card.modifications.length > 0) {
    for (let i = card.modifications.length - 1; i >= 0; i--) {
      detachMod(state, card, i);
    }
  }
  // 2) Снять все keywords.
  if (Array.isArray(card.keywords))           card.keywords.length = 0;
  if (Array.isArray(card.derivedKeywords))    card.derivedKeywords.length = 0;
  if (Array.isArray(card.persistentKeywords)) card.persistentKeywords.length = 0;
  // В.2.d-1: снять все источники Иммунитета (immune — текстовый эффект).
  clearImmunityOnSilence(state, card);
  // C.1: снять Заражение с цели — keyword Лив Лайфа.
  clearInfectedOnSilence(state, card);

  // 3) Лог.
  if (state && state.log) {
    const ownerName = state.players?.[card.ownerId]?.name || '?';
    state.log.add(
      `🤐 ${card.name_ru} [${ownerName}]: Молчание — все способности и Моды сняты ` +
      `(источник: ${sourceId})`
    );
  }

  // 4) Пересчитать ауры.
  if (state) {
    // C.1: Цепь командования удалена. Пересчёт Подкрепления (Глобал).
    recomputeReinforcement(state);
    recomputeLineTriggers(state);
    // В.1.d: уведомить подписчиков (Аура Генератора помех — отключается,
    // если этот юнит — Генератор; враги под его Аурой могли потерять silence-
    // источник).
    notifySilenceChange(state);
  }

  return { ok: true };
}

// Снять источник Молчания. Когда последний источник убран — silenced=false.
// Keywords/Mods НЕ восстанавливаются — Молчание остаётся «деструктивным эффектом
// на момент применения», просто способности юнита снова могут работать.
export function removeSilence(state, card, sourceId = 'default') {
  if (!card?.silenceSources) return { ok: false, reason: 'no_sources' };
  if (!card.silenceSources.has(sourceId)) return { ok: false, reason: 'not_present' };
  card.silenceSources.delete(sourceId);
  if (card.silenceSources.size === 0) {
    if (card.current) card.current.silenced = false;
    if (state?.log) {
      const ownerName = state.players?.[card.ownerId]?.name || '?';
      state.log.add(
        `🔓 ${card.name_ru} [${ownerName}]: Молчание снято (последний источник: ${sourceId})`
      );
    }
    if (state) {
      // C.1: Цепь командования удалена. Пересчёт Подкрепления.
      recomputeReinforcement(state);
      recomputeLineTriggers(state);
      notifySilenceChange(state);
    }
  }
  return { ok: true };
}

// Проверка: текстовые способности заблокированы Молчанием?
export function isSilenced(card) {
  return !!(card && card.current && card.current.silenced);
}

// === Этап 27: TTL silence от Действий (Глушилка, Тотальная глушилка) ===
//
// Хранение: state.silenceTTL[sourceId] = { card, expiresAtTurn }.
// Snake: применять через applySilenceWithTTL → копит в реестр.
// Tick: tickSilenceTTL(state) в начале хода — snimaет источники с
// истекшим сроком через removeSilence.

export function applySilenceWithTTL(state, card, sourceId, turns) {
  const r = applySilence(state, card, sourceId);
  if (!r.ok || turns <= 0) return r;
  if (!state.silenceTTL) state.silenceTTL = {};
  state.silenceTTL[sourceId] = {
    card,
    expiresAtTurn: (state.currentTurn || 0) + turns
  };
  return r;
}

export function tickSilenceTTL(state) {
  if (!state?.silenceTTL) return;
  const expiry = state.silenceTTL;
  const currentTurn = state.currentTurn || 0;
  for (const key of Object.keys(expiry)) {
    const { card, expiresAtTurn } = expiry[key];
    if (currentTurn >= expiresAtTurn) {
      if (card) removeSilence(state, card, key);
      delete expiry[key];
    }
  }
}
